import React, { createContext, useCallback, useContext, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sparkles, Users, Bot, Crown } from "lucide-react";
import { useT } from "@/lib/i18n";

const LoginWallContext = createContext(null);

const ICONS = { default: Sparkles, teammates: Users, coach: Bot, premium: Crown };

export function LoginWallProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [feature, setFeature] = useState("default");
  const [title, setTitle] = useState(null);
  const [sub, setSub] = useState(null);

  const requireAuth = useCallback((opts = {}) => {
    setFeature(opts.feature || "default");
    setTitle(opts.title || null);
    setSub(opts.sub || null);
    setOpen(true);
  }, []);

  return (
    <LoginWallContext.Provider value={{ requireAuth }}>
      {children}
      <LoginWallDialog open={open} setOpen={setOpen} feature={feature} title={title} sub={sub} />
    </LoginWallContext.Provider>
  );
}

function LoginWallDialog({ open, setOpen, feature, title, sub }) {
  const { t } = useT();
  const navigate = useNavigate();
  const Icon = ICONS[feature] || ICONS.default;

  const go = (path) => {
    setOpen(false);
    navigate(path);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="bg-zinc-950 border border-neon/30 text-white rounded-3xl sm:max-w-md shadow-neon-lg p-0 overflow-hidden"
        data-testid="login-wall"
      >
        <div className="relative px-6 pt-8 pb-6">
          <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-neon/20 blur-3xl pointer-events-none" />
          <div className="relative flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-neon/15 border border-neon/30 grid place-items-center text-neon shrink-0">
              <Icon size={22} />
            </div>
            <DialogHeader className="text-left">
              <DialogTitle className="font-display text-2xl font-black leading-tight">
                {title || t("wall.title")}
              </DialogTitle>
              <DialogDescription className="text-zinc-400 mt-1">
                {sub || t("wall.sub")}
              </DialogDescription>
            </DialogHeader>
          </div>
        </div>

        <div className="px-6 pb-6 pt-1 flex flex-col gap-3">
          <Button
            onClick={() => go("/login")}
            className="h-12 rounded-full bg-neon text-black hover:bg-neon-hover font-bold shadow-neon"
            data-testid="wall-signup-btn"
          >
            {t("wall.signup")}
          </Button>
          <div className="text-center text-xs text-zinc-500">
            {t("wall.have_account")}{" "}
            <Link
              to="/login"
              onClick={() => setOpen(false)}
              className="text-neon hover:underline font-semibold"
              data-testid="wall-login-link"
            >
              {t("wall.login")}
            </Link>
          </div>
          <button
            onClick={() => setOpen(false)}
            className="text-center text-xs text-zinc-500 hover:text-zinc-300 mt-1"
            data-testid="wall-close-btn"
          >
            {t("wall.close")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function useLoginWall() {
  const ctx = useContext(LoginWallContext);
  if (!ctx) throw new Error("useLoginWall must be used inside LoginWallProvider");
  return ctx;
}
